// LeaderboardCard — global XP leaderboard rendered on the Profile tab.
// Fetches /api/leaderboard (top 20 + caller's rank) and surfaces a podium
// for the top 3 plus a compact list for ranks 4-20.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { THEME } from "./state";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PODIUM = [
  { idx: 0, label: "1st", color: "#FFD700", trophy: "🥇" },
  { idx: 1, label: "2nd", color: "#C0C0C0", trophy: "🥈" },
  { idx: 2, label: "3rd", color: "#CD7F32", trophy: "🥉" },
];

// Subtle PRO badge — rendered next to the trader name when their account has
// INTEL access (paid / funded). Small, monospace, pink so it pops without
// dominating the row. Pure CSS — zero engineering cost as it scales.
const ProBadge = ({ size = 9 }) => (
  <span data-testid="pro-badge" title="PRO member · Intel Hub access" style={{
    display: "inline-flex", alignItems: "center", gap: 2,
    padding: "1px 6px", marginLeft: 6, borderRadius: 50,
    background: "#f472b620", border: "1px solid #f472b6",
    color: "#f472b6", fontFamily: "monospace", fontSize: size,
    fontWeight: 800, letterSpacing: 0.6, lineHeight: 1.4, verticalAlign: "middle",
  }}>PRO ✦</span>
);

export default function LeaderboardCard({ limit = 20 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const token = localStorage.getItem("flexx_access_token");
        if (!token) { if (mounted) setLoading(false); return; }
        const res = await axios.get(`${API}/leaderboard?limit=${limit}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) { setData(res.data); setLoading(false); }
      } catch (e) {
        if (mounted) { setError(e?.response?.data?.detail || "Failed to load"); setLoading(false); }
      }
    };
    load();
    return () => { mounted = false; };
  }, [limit]);

  if (loading) return (
    <div data-testid="leaderboard-loading" className="flexx-card" style={{ padding: 16, color: THEME.mute, fontFamily: "monospace", fontSize: 11, letterSpacing: 1 }}>
      Loading leaderboard…
    </div>
  );
  if (error) return (
    <div data-testid="leaderboard-error" className="flexx-card" style={{ padding: 16, color: "#FF6B35", fontSize: 12 }}>
      {error}
    </div>
  );
  if (!data) return null;

  const top = data.leaderboard || [];
  const podium = top.slice(0, 3);
  const rest = top.slice(3);
  const me = data.me || {};

  return (
    <div data-testid="leaderboard-card" className="flexx-card" style={{ borderLeft: `3px solid ${THEME.gold}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 10, letterSpacing: 2 }}>🏆 LEADERBOARD</div>
          <div style={{ color: THEME.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>Top Traders by XP</div>
        </div>
        <div data-testid="leaderboard-total" style={{ color: THEME.mute, fontFamily: "monospace", fontSize: 11 }}>
          {data.total} traders
        </div>
      </div>

      {/* Podium — top 3 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
        {PODIUM.map(({ idx, label, color, trophy }) => {
          const r = podium[idx];
          return (
            <div key={idx} data-testid={`leaderboard-podium-${idx + 1}`}
              style={{
                background: r ? `${color}10` : "#0d1218",
                border: `1px solid ${r ? color : THEME.border}`,
                borderRadius: 10, padding: 10, textAlign: "center",
                opacity: r ? 1 : 0.45,
              }}>
              <div style={{ fontSize: 22, lineHeight: 1, marginBottom: 4 }}>{trophy}</div>
              <div style={{ fontFamily: "monospace", color, fontSize: 9, letterSpacing: 1.5 }}>{label}</div>
              <div style={{ color: THEME.text, fontWeight: 800, fontSize: 13, marginTop: 4, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r ? r.name : "—"}{r?.is_pro && <ProBadge size={8} />}
              </div>
              <div style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 11, marginTop: 2 }}>
                {r ? `${r.xp.toLocaleString()} XP` : ""}
              </div>
            </div>
          );
        })}
      </div>

      {/* Rest of leaderboard */}
      {rest.length > 0 && (
        <div data-testid="leaderboard-list" style={{ display: "grid", gap: 4, marginBottom: 14 }}>
          {rest.map((r) => (
            <div key={r.rank} data-testid={`leaderboard-row-${r.rank}`}
              style={{
                display: "grid", gridTemplateColumns: "30px 1fr auto", gap: 10,
                padding: "8px 12px", borderRadius: 6,
                background: r.rank === me.rank ? `${THEME.gold}14` : "transparent",
                border: r.rank === me.rank ? `1px solid ${THEME.gold}55` : "1px solid transparent",
                alignItems: "center",
              }}>
              <span style={{ color: THEME.mute, fontFamily: "monospace", fontSize: 11, fontWeight: 700 }}>{r.rank}</span>
              <span style={{ color: THEME.text, fontSize: 13 }}>{r.name}{r.is_pro && <ProBadge />}</span>
              <span style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 11 }}>{r.xp.toLocaleString()} XP</span>
            </div>
          ))}
        </div>
      )}

      {/* Self rank pill */}
      <div data-testid="leaderboard-me-rank" style={{
        padding: 12, borderRadius: 8,
        background: me.rank ? `${THEME.gold}10` : "#0d1218",
        border: `1px solid ${me.rank ? THEME.gold : THEME.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
      }}>
        <div>
          <div style={{ fontFamily: "monospace", color: THEME.gold, fontSize: 9, letterSpacing: 1.5 }}>YOUR RANK</div>
          <div style={{ color: THEME.text, fontWeight: 800, fontSize: 16, marginTop: 4 }}>
            {me.rank ? `#${me.rank}` : "Unranked"} <span style={{ color: THEME.mute, fontWeight: 400, fontSize: 13 }}>· {me.name}{me.is_pro && <ProBadge />}</span>
          </div>
        </div>
        <div style={{ color: THEME.gold, fontFamily: "monospace", fontSize: 14, fontWeight: 800 }}>
          {me.xp ? `${me.xp.toLocaleString()} XP` : ""}
        </div>
      </div>
    </div>
  );
}
